import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../components/AppIcon';
import { tiendaDb } from '../../lib/supabase';
import {
  epelsaConfigService,
  epelsaLineasNoVinculadasService,
} from '../../services/epelsaService';
import { tpvProductosService, tpvVentaService } from '../../services/tpvService';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const ORANGE = '#FB923C';
const RED = '#F87171';

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = (n) => (n ?? 0)?.toFixed(2);

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Madrid',
    })?.format(new Date(d));
  } catch { return d; }
};

const fmtTime = (d) => {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: 'Europe/Madrid',
    })?.format(new Date(d));
  } catch { return '—'; }
};

const fmtDateOnly = (d) => {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      timeZone: 'Europe/Madrid',
    })?.format(new Date(d));
  } catch { return '—'; }
};

const normalizarFormaPago = (fp) => {
  if (!fp) return '—';
  const v = fp?.toLowerCase()?.trim();
  if (v === 't.credito' || v === 'credito' || v === 'tarjeta' || v === 'credit') return 'Tarjeta';
  if (v === 'efectivo' || v === 'cash' || v === 't.efectivo') return 'Efectivo';
  if (v === 'bizum') return 'Bizum';
  if (v === 'transferencia') return 'Transferencia';
  return fp;
};

const esDevolucion = (ticket) => (ticket?.total ?? 0) < 0;

const getTodayMadrid = () => {
  const now = new Date();
  return now?.toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
};

// Build Madrid timezone offset string for date queries
const madridOffset = () => {
  const now = new Date();
  const madridStr = now?.toLocaleString('en-US', { timeZone: 'Europe/Madrid', hour12: false });
  const utcStr = now?.toLocaleString('en-US', { timeZone: 'UTC', hour12: false });
  const diff = (new Date(madridStr) - new Date(utcStr)) / 3600000;
  const sign = diff >= 0 ? '+' : '-';
  const abs = Math.abs(diff);
  const h = String(Math.floor(abs))?.padStart(2, '0');
  const m = String(Math.round((abs % 1) * 60))?.padStart(2, '0');
  return `${sign}${h}:${m}`;
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, icon, color, sub = undefined }) => (
  <div className="rounded-xl p-4 flex flex-col gap-1"
    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}20` }}>
    <div className="flex items-center justify-between mb-1">
      <span className="font-oranienbaum text-white/40 text-xs">{label}</span>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
        <Icon name={icon} size={13} style={{ color }} />
      </div>
    </div>
    <p className="font-oranienbaum font-bold text-white text-xl">{value}</p>
    {sub && <p className="font-oranienbaum text-white/30 text-[10px]">{sub}</p>}
  </div>
);

// ─── BADGE ESTADO ─────────────────────────────────────────────────────────────
const EstadoBadge = ({ estado, isDev }) => {
  if (isDev) {
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
        style={{ background: `${ORANGE}18`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
        Devolución
      </span>
    );
  }
  const map = {
    VALIDO: { color: ACCENT, label: 'Válido' },
    PROCESADO: { color: ACCENT, label: 'Procesado' },
    ANULADO: { color: RED, label: 'Anulado' },
    ERROR: { color: RED, label: 'Error' },
    PENDIENTE: { color: GOLD, label: 'Pendiente' },
  };
  const cfg = map?.[estado] || { color: 'rgba(255,255,255,0.3)', label: estado || 'Válido' };
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
      style={{ background: `${cfg?.color}18`, color: cfg?.color, border: `1px solid ${cfg?.color}30` }}>
      {cfg?.label}
    </span>
  );
};

// ─── ACTIVE FILTER CHIPS ──────────────────────────────────────────────────────
const FilterChips = ({ filtros, advFiltros, onRemove, onClearAll }) => {
  const chips = [];

  if (filtros?.fechaDesde && filtros?.fechaHasta && filtros?.fechaDesde === filtros?.fechaHasta) {
    chips?.push({ key: 'fecha', label: `Fecha: ${filtros?.fechaDesde}`, onRemove: () => onRemove('fechaDesde', '') || onRemove('fechaHasta', '') });
  } else {
    if (filtros?.fechaDesde) chips?.push({ key: 'fechaDesde', label: `Desde: ${filtros?.fechaDesde}`, onRemove: () => onRemove('fechaDesde', '') });
    if (filtros?.fechaHasta) chips?.push({ key: 'fechaHasta', label: `Hasta: ${filtros?.fechaHasta}`, onRemove: () => onRemove('fechaHasta', '') });
  }
  if (filtros?.ticket) chips?.push({ key: 'ticket', label: `Ticket: ${filtros?.ticket}`, onRemove: () => onRemove('ticket', '') });
  if (filtros?.caja) chips?.push({ key: 'caja', label: `Caja: ${filtros?.caja}`, onRemove: () => onRemove('caja', '') });
  if (filtros?.estado) chips?.push({ key: 'estado', label: `Estado: ${filtros?.estado}`, onRemove: () => onRemove('estado', '') });
  if (filtros?.globalSearch) chips?.push({ key: 'globalSearch', label: `Buscar: "${filtros?.globalSearch}"`, onRemove: () => onRemove('globalSearch', '') });

  if (advFiltros?.articulo) chips?.push({ key: 'articulo', label: `Artículo: ${advFiltros?.articulo}`, onRemove: () => onRemove('articulo', '', true) });
  if (advFiltros?.familias?.length > 0) chips?.push({ key: 'familias', label: `Familias: ${advFiltros?.familias?.join(', ')}`, onRemove: () => onRemove('familias', [], true) });
  if (advFiltros?.vendedor) chips?.push({ key: 'vendedor', label: `Vendedor: ${advFiltros?.vendedor}`, onRemove: () => onRemove('vendedor', '', true) });
  if (advFiltros?.formaPago) chips?.push({ key: 'formaPago', label: `Pago: ${advFiltros?.formaPago}`, onRemove: () => onRemove('formaPago', '', true) });
  if (advFiltros?.tipoOp) chips?.push({ key: 'tipoOp', label: `Tipo: ${advFiltros?.tipoOp}`, onRemove: () => onRemove('tipoOp', '', true) });
  if (advFiltros?.importeMin) chips?.push({ key: 'importeMin', label: `Importe ≥ ${advFiltros?.importeMin} €`, onRemove: () => onRemove('importeMin', '', true) });
  if (advFiltros?.importeMax) chips?.push({ key: 'importeMax', label: `Importe ≤ ${advFiltros?.importeMax} €`, onRemove: () => onRemove('importeMax', '', true) });
  if (advFiltros?.horaDesde) chips?.push({ key: 'horaDesde', label: `Hora ≥ ${advFiltros?.horaDesde}`, onRemove: () => onRemove('horaDesde', '', true) });
  if (advFiltros?.horaHasta) chips?.push({ key: 'horaHasta', label: `Hora ≤ ${advFiltros?.horaHasta}`, onRemove: () => onRemove('horaHasta', '', true) });
  if (advFiltros?.pesoMin) chips?.push({ key: 'pesoMin', label: `Peso ≥ ${advFiltros?.pesoMin} kg`, onRemove: () => onRemove('pesoMin', '', true) });
  if (advFiltros?.pesoMax) chips?.push({ key: 'pesoMax', label: `Peso ≤ ${advFiltros?.pesoMax} kg`, onRemove: () => onRemove('pesoMax', '', true) });
  if (advFiltros?.cantidadMin) chips?.push({ key: 'cantidadMin', label: `Cant ≥ ${advFiltros?.cantidadMin}`, onRemove: () => onRemove('cantidadMin', '', true) });
  if (advFiltros?.cantidadMax) chips?.push({ key: 'cantidadMax', label: `Cant ≤ ${advFiltros?.cantidadMax}`, onRemove: () => onRemove('cantidadMax', '', true) });
  if (advFiltros?.precioMin) chips?.push({ key: 'precioMin', label: `Precio ≥ ${advFiltros?.precioMin} €`, onRemove: () => onRemove('precioMin', '', true) });
  if (advFiltros?.precioMax) chips?.push({ key: 'precioMax', label: `Precio ≤ ${advFiltros?.precioMax} €`, onRemove: () => onRemove('precioMax', '', true) });
  if (advFiltros?.iva) chips?.push({ key: 'iva', label: `IVA: ${advFiltros?.iva}%`, onRemove: () => onRemove('iva', '', true) });
  if (advFiltros?.precioMod) chips?.push({ key: 'precioMod', label: `Precio: ${advFiltros?.precioMod}`, onRemove: () => onRemove('precioMod', '', true) });
  if (advFiltros?.saleForm) chips?.push({ key: 'saleForm', label: `Venta: ${advFiltros?.saleForm}`, onRemove: () => onRemove('saleForm', '', true) });
  if (advFiltros?.factura) chips?.push({ key: 'factura', label: `Factura: ${advFiltros?.factura}`, onRemove: () => onRemove('factura', '', true) });
  if (advFiltros?.conFactura !== '') chips?.push({ key: 'conFactura', label: advFiltros?.conFactura === 'si' ? 'Con factura' : 'Sin factura', onRemove: () => onRemove('conFactura', '', true) });
  if (advFiltros?.lote) chips?.push({ key: 'lote', label: `Lote: ${advFiltros?.lote}`, onRemove: () => onRemove('lote', '', true) });
  if (advFiltros?.cliente) chips?.push({ key: 'cliente', label: `Cliente: ${advFiltros?.cliente}`, onRemove: () => onRemove('cliente', '', true) });

  if (chips?.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {chips?.map(chip => (
        <span key={chip?.key}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-oranienbaum"
          style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30`, color: BLUE }}>
          {chip?.label}
          <button onClick={chip?.onRemove} className="hover:text-white transition-colors">
            <Icon name="X" size={10} />
          </button>
        </span>
      ))}
      <button onClick={onClearAll}
        className="px-2.5 py-1 rounded-full text-[11px] font-oranienbaum transition-all"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)' }}>
        Limpiar todos
      </button>
    </div>
  );
};

// ─── ADVANCED FILTERS PANEL ───────────────────────────────────────────────────
const AdvancedFiltersPanel = ({ advFiltros, setAdvFiltros, onClose, familiasDisponibles, vendedoresDisponibles, ivaDisponibles, saleFormsDisponibles }) => {
  const [local, setLocal] = useState({ ...advFiltros });

  const set = (k, v) => setLocal(prev => ({ ...prev, [k]: v }));

  const toggleFamilia = (f) => {
    const arr = local?.familias || [];
    set('familias', arr?.includes(f) ? arr?.filter(x => x !== f) : [...arr, f]);
  };

  const handleApply = () => {
    setAdvFiltros(local);
    onClose();
  };

  const handleReset = () => {
    const empty = {
      articulo: '', familias: [], vendedor: '', formaPago: '', tipoOp: '',
      importeMin: '', importeMax: '', horaDesde: '', horaHasta: '',
      pesoMin: '', pesoMax: '', cantidadMin: '', cantidadMax: '',
      precioMin: '', precioMax: '', iva: '', precioMod: '', saleForm: '',
      factura: '', conFactura: '', lote: '', cliente: '',
    };
    setLocal(empty);
  };

  const inputCls = "w-full px-2.5 py-2 rounded-lg text-xs font-oranienbaum text-white/70 placeholder-white/20 outline-none";
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const labelCls = "font-oranienbaum text-white/40 text-[10px] mb-1 block uppercase tracking-wider";
  const sectionCls = "border-b pb-4 mb-4";
  const sectionBorder = { borderColor: 'rgba(255,255,255,0.06)' };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.60)' }} onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto flex flex-col"
        style={{ background: '#111', borderLeft: '1px solid rgba(255,255,255,0.10)' }}
        onClick={e => e?.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10"
          style={{ borderColor: 'rgba(255,255,255,0.08)', background: '#111' }}>
          <div className="flex items-center gap-2">
            <Icon name="SlidersHorizontal" size={16} style={{ color: BLUE }} />
            <h3 className="font-oranienbaum font-bold text-white text-base">Filtros avanzados</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-0">
          {/* Artículo */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Artículo</label>
            <input type="text" placeholder="Descripción o código artículo..." value={local?.articulo}
              onChange={e => set('articulo', e?.target?.value)}
              className={inputCls} style={inputStyle} />
          </div>

          {/* Familia */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Familia</label>
            <div className="flex flex-wrap gap-2">
              {familiasDisponibles?.map(f => (
                <button key={f} onClick={() => toggleFamilia(f)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-oranienbaum transition-all"
                  style={(local?.familias || [])?.includes(f)
                    ? { background: `${PURPLE}20`, border: `1px solid ${PURPLE}40`, color: PURPLE }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  Familia {f}
                </button>
              ))}
              {familiasDisponibles?.length === 0 && (
                <span className="font-oranienbaum text-white/25 text-xs">Cargando familias...</span>
              )}
            </div>
          </div>

          {/* Vendedor */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Vendedor</label>
            <input type="text" placeholder="Nombre o código vendedor..." value={local?.vendedor}
              onChange={e => set('vendedor', e?.target?.value)}
              className={inputCls} style={inputStyle} />
            {vendedoresDisponibles?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {vendedoresDisponibles?.map(v => (
                  <button key={v} onClick={() => set('vendedor', v)}
                    className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum transition-all"
                    style={local?.vendedor === v
                      ? { background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                    {v}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Forma de pago */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Forma de pago</label>
            <div className="flex flex-wrap gap-2">
              {['', 'Efectivo', 'Tarjeta', 'Bizum', 'Transferencia']?.map(fp => (
                <button key={fp} onClick={() => set('formaPago', fp)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-oranienbaum transition-all"
                  style={local?.formaPago === fp
                    ? { background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {fp || 'Todas'}
                </button>
              ))}
            </div>
          </div>

          {/* Tipo de operación */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Tipo de operación</label>
            <div className="flex gap-2">
              {[{ v: '', l: 'Todas' }, { v: 'ventas', l: 'Ventas' }, { v: 'devoluciones', l: 'Devoluciones' }]?.map(o => (
                <button key={o?.v} onClick={() => set('tipoOp', o?.v)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                  style={local?.tipoOp === o?.v
                    ? { background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {o?.l}
                </button>
              ))}
            </div>
          </div>

          {/* Importe */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Importe del ticket</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Mínimo (€)</label>
                <input type="number" placeholder="0" value={local?.importeMin}
                  onChange={e => set('importeMin', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Máximo (€)</label>
                <input type="number" placeholder="∞" value={local?.importeMax}
                  onChange={e => set('importeMax', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[['< 5 €', '', '5'], ['5–10 €', '5', '10'], ['10–20 €', '10', '20'], ['20–50 €', '20', '50'], ['> 50 €', '50', '']]?.map(([l, mn, mx]) => (
                <button key={l} onClick={() => { set('importeMin', mn); set('importeMax', mx); }}
                  className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Hora */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Hora (Europe/Madrid)</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Desde</label>
                <input type="time" value={local?.horaDesde}
                  onChange={e => set('horaDesde', e?.target?.value)}
                  className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
              </div>
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Hasta</label>
                <input type="time" value={local?.horaHasta}
                  onChange={e => set('horaHasta', e?.target?.value)}
                  className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[['Mañana', '07:00', '12:00'], ['Mediodía', '12:00', '15:00'], ['Tarde', '15:00', '20:00'], ['Noche', '20:00', '23:59']]?.map(([l, h1, h2]) => (
                <button key={l} onClick={() => { set('horaDesde', h1); set('horaHasta', h2); }}
                  className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Peso */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Peso (kg)</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Mínimo</label>
                <input type="number" step="0.001" placeholder="0" value={local?.pesoMin}
                  onChange={e => set('pesoMin', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Máximo</label>
                <input type="number" step="0.001" placeholder="∞" value={local?.pesoMax}
                  onChange={e => set('pesoMax', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Cantidad */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Cantidad</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Mínima</label>
                <input type="number" placeholder="0" value={local?.cantidadMin}
                  onChange={e => set('cantidadMin', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Máxima</label>
                <input type="number" placeholder="∞" value={local?.cantidadMax}
                  onChange={e => set('cantidadMax', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Precio unitario */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Precio unitario (€)</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Mínimo</label>
                <input type="number" placeholder="0" value={local?.precioMin}
                  onChange={e => set('precioMin', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className="font-oranienbaum text-white/30 text-[10px] mb-1 block">Máximo</label>
                <input type="number" placeholder="∞" value={local?.precioMax}
                  onChange={e => set('precioMax', e?.target?.value)}
                  className={inputCls} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* IVA */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>IVA (%)</label>
            <div className="flex flex-wrap gap-2">
              {['', ...ivaDisponibles]?.map(iv => (
                <button key={iv} onClick={() => set('iva', iv)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-oranienbaum transition-all"
                  style={local?.iva === iv
                    ? { background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {iv === '' ? 'Todos' : `${iv}%`}
                </button>
              ))}
            </div>
          </div>

          {/* Precio modificado */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Precio modificado</label>
            <div className="flex gap-2">
              {[{ v: '', l: 'Todos' }, { v: 'normal', l: 'Precio normal' }, { v: 'modificado', l: 'Precio modificado' }]?.map(o => (
                <button key={o?.v} onClick={() => set('precioMod', o?.v)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                  style={local?.precioMod === o?.v
                    ? { background: `${ORANGE}20`, border: `1px solid ${ORANGE}40`, color: ORANGE }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {o?.l}
                </button>
              ))}
            </div>
          </div>

          {/* Venta por peso/unidad */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Tipo de venta</label>
            <div className="flex gap-2">
              {[{ v: '', l: 'Todas' }, ...saleFormsDisponibles?.map(s => ({ v: s, l: s }))]?.map(o => (
                <button key={o?.v} onClick={() => set('saleForm', o?.v)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                  style={local?.saleForm === o?.v
                    ? { background: `${PURPLE}20`, border: `1px solid ${PURPLE}40`, color: PURPLE }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {o?.l || 'Todas'}
                </button>
              ))}
            </div>
          </div>

          {/* Factura */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Factura</label>
            <input type="text" placeholder="Nº factura..." value={local?.factura}
              onChange={e => set('factura', e?.target?.value)}
              className={`${inputCls} mb-2`} style={inputStyle} />
            <div className="flex gap-2">
              {[{ v: '', l: 'Todas' }, { v: 'si', l: 'Con factura' }, { v: 'no', l: 'Sin factura' }]?.map(o => (
                <button key={o?.v} onClick={() => set('conFactura', o?.v)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                  style={local?.conFactura === o?.v
                    ? { background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}>
                  {o?.l}
                </button>
              ))}
            </div>
          </div>

          {/* Más filtros: Cliente y Lote */}
          <div className={sectionCls} style={sectionBorder}>
            <label className={labelCls}>Cliente</label>
            <input type="text" placeholder="Nombre, NIF o código..." value={local?.cliente}
              onChange={e => set('cliente', e?.target?.value)}
              className={inputCls} style={inputStyle} />
          </div>

          <div className="pb-4">
            <label className={labelCls}>Lote bovino</label>
            <input type="text" placeholder="Nº lote..." value={local?.lote}
              onChange={e => set('lote', e?.target?.value)}
              className={inputCls} style={inputStyle} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex gap-2 sticky bottom-0"
          style={{ borderColor: 'rgba(255,255,255,0.08)', background: '#111' }}>
          <button onClick={handleReset}
            className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
            Limpiar
          </button>
          <button onClick={handleApply}
            className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold transition-all"
            style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
            Aplicar filtros
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL DETALLE TICKET ─────────────────────────────────────────────────────
const TicketDetailModal = ({ ticket, onClose }) => {
  const [lineas, setLineas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticket?.id) return;
    setLoading(true);
    Promise.all([
      tiendaDb?.from('epelsa_ticket_lineas')
        ?.select('id, articulo_codigo, descripcion, cantidad, peso, precio_unitario, total, base_imponible, iva_porcentaje, iva_importe, descuento, sale_form, familia_codigo, lote_bovino, precio_modificado')
        ?.eq('ticket_id', ticket?.id)
        ?.order('created_at', { ascending: true }),
      tiendaDb?.from('epelsa_ticket_pagos')
        ?.select('id, orden, metodo_codigo, metodo_nombre, importe_entregado')
        ?.eq('ticket_id', ticket?.id)
        ?.order('orden', { ascending: true }),
    ])?.then(([lineasRes, pagosRes]) => {
      setLineas(lineasRes?.data || []);
      setPagos(pagosRes?.data || []);
    })?.catch(() => {})?.finally(() => setLoading(false));
  }, [ticket?.id]);

  if (!ticket) return null;
  const isDev = esDevolucion(ticket);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.80)' }}>
      <div className="rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: '#111', border: '1px solid rgba(255,255,255,0.12)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-oranienbaum font-bold text-white text-lg">
                Ticket {ticket?.numero_ticket || ticket?.epelsa_id || '—'}
              </h3>
              {isDev && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
                  style={{ background: `${ORANGE}18`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
                  Devolución
                </span>
              )}
            </div>
            <p className="font-oranienbaum text-white/40 text-xs">{fmtDate(ticket?.fecha)}</p>
          </div>
          <div className="flex items-center gap-3">
            <EstadoBadge estado={ticket?.estado} isDev={isDev} />
            <button onClick={onClose} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {[
            { label: 'Nº Ticket', value: ticket?.numero_ticket || '—' },
            { label: 'Fecha', value: fmtDateOnly(ticket?.fecha) },
            { label: 'Hora', value: fmtTime(ticket?.fecha) },
            { label: 'Vendedor', value: ticket?.vendedor_nombre || ticket?.vendedor_codigo || '—' },
            { label: 'Caja / Balanza', value: ticket?.counter_nombre || ticket?.balanza_codigo || ticket?.counter_codigo || '—' },
            { label: 'Total', value: `${fmt(ticket?.total)} €` },
            { label: 'Base imponible', value: `${fmt(ticket?.base_imponible)} €` },
            { label: 'IVA', value: `${fmt(ticket?.iva_importe)} €` },
            { label: 'Nº líneas', value: ticket?.lineas_numero ?? lineas?.length ?? '—' },
            { label: 'Nº Factura', value: ticket?.numero_factura || '—' },
            { label: 'Cliente', value: ticket?.cliente_nombre || ticket?.cliente_codigo || '—' },
          ]?.map((m, i) => (
            <div key={i}>
              <p className="font-oranienbaum text-white/35 text-[10px] mb-0.5">{m?.label}</p>
              <p className="font-oranienbaum text-white text-sm">{m?.value}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 gap-2">
            <div className="w-5 h-5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
            <span className="font-oranienbaum text-white/30 text-sm">Cargando detalle...</span>
          </div>
        ) : (
          <>
            {pagos?.length > 0 && (
              <div className="px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <p className="font-oranienbaum text-white/50 text-xs mb-3 uppercase tracking-widest">Formas de pago</p>
                <div className="space-y-2">
                  {pagos?.map((p, i) => (
                    <div key={p?.id || i} className="flex items-center justify-between px-3 py-2 rounded-lg"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="flex items-center gap-2">
                        <Icon name="CreditCard" size={12} className="text-white/40" />
                        <span className="font-oranienbaum text-white/70 text-sm">
                          {normalizarFormaPago(p?.metodo_nombre || p?.metodo_codigo)}
                        </span>
                        {p?.metodo_codigo && (
                          <span className="font-oranienbaum text-white/25 text-[10px]">({p?.metodo_codigo})</span>
                        )}
                      </div>
                      <span className="font-oranienbaum font-semibold text-white text-sm">
                        {fmt(p?.importe_entregado)} €
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-6 py-4">
              <p className="font-oranienbaum text-white/50 text-xs mb-3 uppercase tracking-widest">
                Líneas ({lineas?.length})
              </p>
              {lineas?.length === 0 ? (
                <p className="font-oranienbaum text-white/25 text-sm">Sin líneas disponibles</p>
              ) : (
                <div className="space-y-2">
                  {lineas?.map((l, i) => (
                    <div key={l?.id || i} className="rounded-xl p-3"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="font-oranienbaum text-white/80 text-sm">{l?.descripcion || '—'}</p>
                          <div className="flex flex-wrap gap-3 mt-1">
                            {l?.articulo_codigo && <span className="font-oranienbaum text-white/35 text-[10px]">Cód: {l?.articulo_codigo}</span>}
                            {l?.familia_codigo && <span className="font-oranienbaum text-white/35 text-[10px]">Fam: {l?.familia_codigo}</span>}
                            {l?.cantidad != null && l?.cantidad !== 0 && <span className="font-oranienbaum text-white/35 text-[10px]">Cant: {l?.cantidad}</span>}
                            {l?.peso != null && l?.peso !== 0 && <span className="font-oranienbaum text-amber-400/70 text-[10px]">⚖ {l?.peso} kg</span>}
                            {l?.precio_unitario != null && <span className="font-oranienbaum text-white/35 text-[10px]">{fmt(l?.precio_unitario)} €/u</span>}
                            {l?.iva_porcentaje != null && <span className="font-oranienbaum text-white/35 text-[10px]">IVA: {l?.iva_porcentaje}%</span>}
                            {l?.descuento != null && l?.descuento !== 0 && <span className="font-oranienbaum text-orange-400/70 text-[10px]">Dto: {l?.descuento}%</span>}
                            {l?.sale_form && <span className="font-oranienbaum text-white/25 text-[10px]">{l?.sale_form}</span>}
                            {l?.lote_bovino && <span className="font-oranienbaum text-amber-300/60 text-[10px]">Lote: {l?.lote_bovino}</span>}
                            {l?.precio_modificado && <span className="font-oranienbaum text-orange-300/70 text-[10px]">Precio mod.</span>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-oranienbaum font-bold text-white text-sm">{fmt(l?.total)} €</p>
                          {l?.base_imponible != null && <p className="font-oranienbaum text-white/35 text-[10px]">Base: {fmt(l?.base_imponible)} €</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── MODAL VENTA EMERGENCIA ───────────────────────────────────────────────────
const EmergencySaleModal = ({ onClose }) => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [cantidad, setCantidad] = useState('');
  const [formaPago, setFormaPago] = useState('EFECTIVO');
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState(null);
  const [ventaOk, setVentaOk] = useState(null);

  useEffect(() => {
    tpvProductosService?.getProductosConStock({ search })?.then(setProductos)?.catch(() => {})?.finally(() => setLoading(false));
  }, [search]);

  const total = selectedProducto && cantidad
    ? parseFloat((parseFloat(cantidad?.replace(',', '.')) * (selectedProducto?.pvp_venta ?? 0))?.toFixed(2))
    : 0;

  const handleVender = async () => {
    if (!selectedProducto || !cantidad) return;
    const qty = parseFloat(cantidad?.replace(',', '.'));
    if (!qty || qty <= 0) return;
    setProcesando(true);
    setError(null);
    try {
      const tipoIva = selectedProducto?.tipo_iva ?? 10;
      const subtotal = parseFloat((qty * (selectedProducto?.pvp_venta ?? 0))?.toFixed(2));
      const iva = parseFloat((subtotal - subtotal / (1 + tipoIva / 100))?.toFixed(2));
      const base = subtotal - iva;
      const venta = await tpvVentaService?.procesarVenta({
        lineas: [{
          productoId: selectedProducto?.id,
          nombre: selectedProducto?.nombre,
          precioUnitario: selectedProducto?.pvp_venta ?? 0,
          cantidad: qty,
          subtotal,
          tipoIva,
          ventaPorPeso: selectedProducto?.venta_por_peso ?? false,
          stock: selectedProducto?.stock_disponible ?? 0,
        }],
        formaPago,
        baseImponible: base,
        iva,
        total: subtotal,
        origen: 'EMERGENCIA',
      });
      setVentaOk(venta);
    } catch (e) {
      setError(e?.message || 'Error al procesar la venta');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="rounded-2xl w-full max-w-lg"
        style={{ background: '#111', border: `1px solid ${ORANGE}30` }}>
        <div className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: `${ORANGE}15`, border: `1px solid ${ORANGE}30` }}>
              <Icon name="AlertTriangle" size={14} style={{ color: ORANGE }} />
            </div>
            <h3 className="font-oranienbaum font-bold text-white text-base">Venta Manual de Emergencia</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>
        {ventaOk ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="CheckCircle" size={28} style={{ color: ACCENT }} />
            </div>
            <h4 className="font-oranienbaum font-bold text-white text-lg mb-1">¡Venta registrada!</h4>
            <p className="font-oranienbaum text-white/40 text-sm mb-2">{ventaOk?.numero_venta}</p>
            <p className="font-oranienbaum font-bold text-white text-3xl mb-6">{fmt(ventaOk?.total)} €</p>
            <button onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold"
              style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
              Cerrar
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <p className="font-oranienbaum text-orange-300/70 text-xs">
              Usar solo si la báscula Epelsa falla. Esta venta se registrará con origen EMERGENCIA.
            </p>
            <div>
              <label className="font-oranienbaum text-white/50 text-xs mb-1 block">Producto</label>
              <input type="text" placeholder="Buscar producto..." value={search}
                onChange={e => { setSearch(e?.target?.value); setSelectedProducto(null); }}
                className="w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
              {search && !selectedProducto && (
                <div className="mt-1 rounded-xl overflow-hidden max-h-40 overflow-y-auto"
                  style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.10)' }}>
                  {loading ? (
                    <p className="px-3 py-2 font-oranienbaum text-white/30 text-xs">Cargando...</p>
                  ) : productos?.length === 0 ? (
                    <p className="px-3 py-2 font-oranienbaum text-white/30 text-xs">Sin resultados</p>
                  ) : productos?.map(p => (
                    <button key={p?.id} onClick={() => { setSelectedProducto(p); setSearch(p?.nombre); }}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors flex items-center justify-between">
                      <span className="font-oranienbaum text-white/70 text-xs">{p?.nombre}</span>
                      <span className="font-oranienbaum text-white/40 text-xs">{fmt(p?.pvp_venta)} €{p?.venta_por_peso ? '/kg' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="font-oranienbaum text-white/50 text-xs mb-1 block">
                {selectedProducto?.venta_por_peso ? 'Peso (kg)' : 'Cantidad'}
              </label>
              <input type="number" step={selectedProducto?.venta_por_peso ? '0.001' : '1'} min="0"
                value={cantidad} onChange={e => setCantidad(e?.target?.value)}
                placeholder={selectedProducto?.venta_por_peso ? '0.000' : '1'}
                className="w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
            </div>
            <div>
              <label className="font-oranienbaum text-white/50 text-xs mb-1 block">Forma de pago</label>
              <div className="flex gap-2">
                {['EFECTIVO', 'TARJETA', 'BIZUM']?.map(fp => (
                  <button key={fp} onClick={() => setFormaPago(fp)}
                    className="flex-1 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                    style={formaPago === fp
                      ? { background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
                    {fp}
                  </button>
                ))}
              </div>
            </div>
            {total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="font-oranienbaum text-white/50 text-sm">Total</span>
                <span className="font-oranienbaum font-bold text-white text-xl">{fmt(total)} €</span>
              </div>
            )}
            {error && <p className="font-oranienbaum text-red-400 text-xs px-1">{error}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
                Cancelar
              </button>
              <button onClick={handleVender}
                disabled={procesando || !selectedProducto || !cantidad || total <= 0}
                className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: `${ORANGE}20`, border: `1px solid ${ORANGE}40`, color: ORANGE }}>
                {procesando ? 'Procesando...' : 'Registrar venta'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── SECCIÓN: ESTADO CONEXIÓN ─────────────────────────────────────────────────
const ConexionEpelsa = ({ config, conexionOk, onRefresh }) => {
  const [reintentando, setReintentando] = useState(false);
  const handleReintentar = async () => {
    setReintentando(true);
    await new Promise(r => setTimeout(r, 1200));
    setReintentando(false);
    onRefresh?.();
  };
  return (
    <div className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full"
            style={{ background: conexionOk ? ACCENT : RED, boxShadow: `0 0 6px ${conexionOk ? ACCENT : RED}` }} />
          <h3 className="font-oranienbaum font-semibold text-white text-sm">
            {conexionOk ? 'Conectado' : 'Error de conexión'}
          </h3>
        </div>
        <button onClick={handleReintentar} disabled={reintentando}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-50"
          style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}25`, color: BLUE }}>
          <Icon name={reintentando ? 'Loader' : 'RefreshCw'} size={12} className={reintentando ? 'animate-spin' : ''} />
          Reintentar
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Estado Supabase', value: conexionOk ? 'Conectado' : 'Error de conexión' },
          { label: 'Modelo', value: config?.modelo || '—' },
          { label: 'IP', value: config?.ip || '—' },
          { label: 'Puerto', value: config?.puerto || '—' },
          { label: 'Protocolo', value: config?.protocolo || '—' },
          { label: 'Última sincronización', value: fmtDate(config?.ultima_sincronizacion) },
          { label: 'Último ticket recibido', value: config?.ultimo_ticket_recibido || '—' },
        ]?.map((item, i) => (
          <div key={i}>
            <p className="font-oranienbaum text-white/30 text-[10px] mb-0.5">{item?.label}</p>
            <p className="font-oranienbaum text-white/70 text-xs"
              style={item?.label === 'Estado Supabase' ? { color: conexionOk ? ACCENT : RED } : {}}>
              {item?.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── SECCIÓN: PRODUCTOS NO VINCULADOS ────────────────────────────────────────
const ProductosNoVinculados = ({ items, onVincular, onIgnorar }) => {
  const [productosERP, setProductosERP] = useState({});
  const [searchERP, setSearchERP] = useState({});
  const [selectedERP, setSelectedERP] = useState({});
  const [vinculando, setVinculando] = useState({});

  const buscarERP = useCallback(async (id, q) => {
    if (!q || q?.length < 2) { setProductosERP(prev => ({ ...prev, [id]: [] })); return; }
    try {
      const res = await tpvProductosService?.getProductosConStock({ search: q });
      setProductosERP(prev => ({ ...prev, [id]: res || [] }));
    } catch { }
  }, []);

  const handleVincular = async (item) => {
    const prod = selectedERP?.[item?.id];
    if (!prod) return;
    setVinculando(prev => ({ ...prev, [item?.id]: true }));
    try { await onVincular(item?.id, prod?.id); }
    finally { setVinculando(prev => ({ ...prev, [item?.id]: false })); }
  };

  if (items?.length === 0) {
    return (
      <div className="rounded-2xl p-6 flex flex-col items-center gap-2"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <Icon name="CheckCircle" size={24} style={{ color: ACCENT }} />
        <p className="font-oranienbaum text-white/50 text-sm">No hay productos sin vincular</p>
        <p className="font-oranienbaum text-white/25 text-xs text-center">
          Esta sección se utilizará para relacionar referencias Epelsa con productos del ERP
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items?.map(item => (
        <div key={item?.id} className="rounded-xl p-4"
          style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${ORANGE}20` }}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="font-oranienbaum text-white/80 text-sm">{item?.nombre_epelsa || '—'}</p>
              <div className="flex gap-3 mt-1">
                {item?.plu && <span className="font-oranienbaum text-white/35 text-[10px]">PLU: {item?.plu}</span>}
                {item?.ean && <span className="font-oranienbaum text-white/35 text-[10px]">EAN: {item?.ean}</span>}
                {item?.importe != null && <span className="font-oranienbaum text-white/35 text-[10px]">{fmt(item?.importe)} €</span>}
              </div>
            </div>
            <button onClick={() => onIgnorar(item?.id)}
              className="p-1.5 rounded-lg text-white/25 hover:text-white/50 transition-colors flex-shrink-0">
              <Icon name="X" size={13} />
            </button>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input type="text" placeholder="Buscar producto ERP..."
                value={searchERP?.[item?.id] || ''}
                onChange={e => {
                  const v = e?.target?.value;
                  setSearchERP(prev => ({ ...prev, [item?.id]: v }));
                  setSelectedERP(prev => ({ ...prev, [item?.id]: null }));
                  buscarERP(item?.id, v);
                }}
                className="w-full px-3 py-2 rounded-lg text-xs font-oranienbaum text-white/70 placeholder-white/25 outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
              {searchERP?.[item?.id] && !selectedERP?.[item?.id] && (productosERP?.[item?.id] || [])?.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-10 rounded-lg overflow-hidden max-h-32 overflow-y-auto mt-1"
                  style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.10)' }}>
                  {(productosERP?.[item?.id] || [])?.map(p => (
                    <button key={p?.id}
                      onClick={() => { setSelectedERP(prev => ({ ...prev, [item?.id]: p })); setSearchERP(prev => ({ ...prev, [item?.id]: p?.nombre })); }}
                      className="w-full text-left px-3 py-1.5 hover:bg-white/5 transition-colors">
                      <span className="font-oranienbaum text-white/70 text-xs">{p?.nombre}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => handleVincular(item)}
              disabled={!selectedERP?.[item?.id] || vinculando?.[item?.id]}
              className="px-3 py-2 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}>
              {vinculando?.[item?.id] ? '...' : 'Vincular'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── SORT HEADER ──────────────────────────────────────────────────────────────
const SortHeader = ({ label, field, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <th className="px-4 py-3 text-left cursor-pointer select-none group"
      onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        <span className="font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest group-hover:text-white/60 transition-colors">
          {label}
        </span>
        {active ? (
          <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={10} style={{ color: BLUE }} />
        ) : (
          <Icon name="ChevronsUpDown" size={10} className="text-white/20 group-hover:text-white/40 transition-colors" />
        )}
      </div>
    </th>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const TPVFisico = () => {
  const [activeSection, setActiveSection] = useState('tickets');
  const [showEmergency, setShowEmergency] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [vistaMode, setVistaMode] = useState('tickets'); // 'tickets' | 'articulos'

  // Datos
  const [config, setConfig] = useState(null);
  const [kpis, setKpis] = useState({ ventasHoy: 0, ticketsHoy: 0, ticketMedio: 0, efectivo: 0, tarjeta: 0, bizum: 0 });
  const [tickets, setTickets] = useState([]);
  const [lineasView, setLineasView] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [noVinculados, setNoVinculados] = useState([]);
  const [conexionOk, setConexionOk] = useState(null);

  // Dynamic filter options from real data
  const [familiasDisponibles, setFamiliasDisponibles] = useState([]);
  const [vendedoresDisponibles, setVendedoresDisponibles] = useState([]);
  const [ivaDisponibles, setIvaDisponibles] = useState([]);
  const [saleFormsDisponibles, setSaleFormsDisponibles] = useState([]);

  // Loading
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingKpis, setLoadingKpis] = useState(true);

  // Filtros básicos
  const [filtros, setFiltros] = useState({
    fechaDesde: '', fechaHasta: '', ticket: '', caja: '', estado: '', globalSearch: '',
  });

  // Filtros avanzados
  const [advFiltros, setAdvFiltros] = useState({
    articulo: '', familias: [], vendedor: '', formaPago: '', tipoOp: '',
    importeMin: '', importeMax: '', horaDesde: '', horaHasta: '',
    pesoMin: '', pesoMax: '', cantidadMin: '', cantidadMax: '',
    precioMin: '', precioMax: '', iva: '', precioMod: '', saleForm: '',
    factura: '', conFactura: '', lote: '', cliente: '',
  });

  // Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Ordenación
  const [sortField, setSortField] = useState('fecha');
  const [sortDir, setSortDir] = useState('desc');

  // Detect if any line-level filter is active (requires subquery approach)
  const hasLineFilter = useCallback(() => {
    return !!(advFiltros?.articulo || advFiltros?.familias?.length > 0 ||
      advFiltros?.pesoMin || advFiltros?.pesoMax || advFiltros?.cantidadMin || advFiltros?.cantidadMax ||
      advFiltros?.precioMin || advFiltros?.precioMax || advFiltros?.iva || advFiltros?.precioMod ||
      advFiltros?.saleForm || advFiltros?.lote || filtros?.globalSearch);
  }, [advFiltros, filtros?.globalSearch]);

  // ── Load dynamic filter options ────────────────────────────────────────────
  const cargarOpcionesFiltros = useCallback(async () => {
    try {
      const [famRes, vendRes, ivaRes, sfRes] = await Promise.all([
        tiendaDb?.from('epelsa_ticket_lineas')?.select('familia_codigo')?.not('familia_codigo', 'is', null)?.limit(500),
        tiendaDb?.from('epelsa_tickets')?.select('vendedor_nombre, vendedor_codigo')?.not('vendedor_nombre', 'is', null)?.limit(200),
        tiendaDb?.from('epelsa_ticket_lineas')?.select('iva_porcentaje')?.not('iva_porcentaje', 'is', null)?.limit(500),
        tiendaDb?.from('epelsa_ticket_lineas')?.select('sale_form')?.not('sale_form', 'is', null)?.limit(200),
      ]);
      const familias = [...new Set((famRes?.data || []).map(r => r.familia_codigo).filter(Boolean))]?.sort();
      setFamiliasDisponibles(familias);
      const vendedores = [...new Set((vendRes?.data || []).map(r => r.vendedor_nombre || r.vendedor_codigo).filter(Boolean))]?.sort();
      setVendedoresDisponibles(vendedores);
      const ivas = [...new Set((ivaRes?.data || []).map(r => r.iva_porcentaje).filter(v => v != null))]?.sort((a, b) => a - b);
      setIvaDisponibles(ivas);
      const sfs = [...new Set((sfRes?.data || []).map(r => r.sale_form).filter(Boolean))]?.sort();
      setSaleFormsDisponibles(sfs);
    } catch { }
  }, []);

  // ── Build ticket IDs from line filters (for line-level filtering) ──────────
  const getTicketIdsFromLineFilters = useCallback(async () => {
    let q = tiendaDb?.from('epelsa_ticket_lineas')?.select('ticket_id');

    if (advFiltros?.articulo) {
      q = q?.or(`descripcion.ilike.%${advFiltros?.articulo}%,articulo_codigo.ilike.%${advFiltros?.articulo}%`);
    }
    if (advFiltros?.familias?.length > 0) {
      q = q?.in('familia_codigo', advFiltros?.familias);
    }
    if (advFiltros?.pesoMin) q = q?.gte('peso', parseFloat(advFiltros?.pesoMin));
    if (advFiltros?.pesoMax) q = q?.lte('peso', parseFloat(advFiltros?.pesoMax));
    if (advFiltros?.cantidadMin) q = q?.gte('cantidad', parseFloat(advFiltros?.cantidadMin));
    if (advFiltros?.cantidadMax) q = q?.lte('cantidad', parseFloat(advFiltros?.cantidadMax));
    if (advFiltros?.precioMin) q = q?.gte('precio_unitario', parseFloat(advFiltros?.precioMin));
    if (advFiltros?.precioMax) q = q?.lte('precio_unitario', parseFloat(advFiltros?.precioMax));
    if (advFiltros?.iva) q = q?.eq('iva_porcentaje', parseFloat(advFiltros?.iva));
    if (advFiltros?.precioMod === 'modificado') q = q?.eq('precio_modificado', true);
    if (advFiltros?.precioMod === 'normal') q = q?.eq('precio_modificado', false);
    if (advFiltros?.saleForm) q = q?.eq('sale_form', advFiltros?.saleForm);
    if (advFiltros?.lote) q = q?.ilike('lote_bovino', `%${advFiltros?.lote}%`);

    // Global search on lines
    if (filtros?.globalSearch) {
      q = q?.or(`descripcion.ilike.%${filtros?.globalSearch}%,articulo_codigo.ilike.%${filtros?.globalSearch}%,lote_bovino.ilike.%${filtros?.globalSearch}%`);
    }

    const { data } = await q?.limit(2000);
    return [...new Set((data || []).map(r => r.ticket_id))];
  }, [advFiltros, filtros?.globalSearch]);

  // ── Build ticket IDs from payment method filter ────────────────────────────
  const getTicketIdsFromPaymentFilter = useCallback(async () => {
    if (!advFiltros?.formaPago) return null;
    const metodoMap = {
      'Efectivo': ['efectivo', 't.efectivo', 'cash'],
      'Tarjeta': ['t.credito', 'credito', 'tarjeta', 'credit'],
      'Bizum': ['bizum'],
      'Transferencia': ['transferencia'],
    };
    const metodos = metodoMap?.[advFiltros?.formaPago] || [advFiltros?.formaPago?.toLowerCase()];
    let q = tiendaDb?.from('epelsa_ticket_pagos')?.select('ticket_id');
    // Use OR for all possible method names
    const orClauses = metodos?.map(m => `metodo_nombre.ilike.%${m}%`)?.join(',');
    q = q?.or(orClauses);
    const { data } = await q?.limit(2000);
    return [...new Set((data || []).map(r => r.ticket_id))];
  }, [advFiltros?.formaPago]);

  // ── Apply Madrid timezone offset to date+time ──────────────────────────────
  const buildDateTimeFilter = (dateStr, timeStr, isEnd) => {
    if (!dateStr) return null;
    const offset = madridOffset();
    const time = timeStr || (isEnd ? '23:59:59' : '00:00:00');
    return `${dateStr}T${time}${offset}`;
  };

  // ── KPIs: react to current filters ────────────────────────────────────────
  const cargarKpis = useCallback(async () => {
    setLoadingKpis(true);
    try {
      const offset = madridOffset();
      const today = getTodayMadrid();

      // Determine date range for KPIs
      const fechaDesde = filtros?.fechaDesde
        ? `${filtros?.fechaDesde}T00:00:00${offset}`
        : `${today}T00:00:00${offset}`;
      const fechaHasta = filtros?.fechaHasta
        ? `${filtros?.fechaHasta}T23:59:59${offset}`
        : `${today}T23:59:59${offset}`;

      // If line-level filters active, get ticket IDs first
      let ticketIdsFromLines = null;
      if (hasLineFilter()) {
        ticketIdsFromLines = await getTicketIdsFromLineFilters();
        if (ticketIdsFromLines?.length === 0) {
          setKpis({ ventasHoy: 0, ticketsHoy: 0, ticketMedio: 0, efectivo: 0, tarjeta: 0, bizum: 0 });
          setConexionOk(true);
          setLoadingKpis(false);
          return;
        }
      }

      let ticketIdsFromPayment = null;
      if (advFiltros?.formaPago) {
        ticketIdsFromPayment = await getTicketIdsFromPaymentFilter();
        if (ticketIdsFromPayment?.length === 0) {
          setKpis({ ventasHoy: 0, ticketsHoy: 0, ticketMedio: 0, efectivo: 0, tarjeta: 0, bizum: 0 });
          setConexionOk(true);
          setLoadingKpis(false);
          return;
        }
      }

      let q = tiendaDb?.from('epelsa_tickets')?.select('id, total, forma_pago, estado')
        ?.gte('fecha', fechaDesde)?.lte('fecha', fechaHasta);

      if (filtros?.caja) q = q?.or(`balanza_codigo.ilike.%${filtros?.caja}%,counter_codigo.ilike.%${filtros?.caja}%,counter_nombre.ilike.%${filtros?.caja}%`);
      if (filtros?.estado) {
        if (filtros?.estado === 'DEVOLUCION') q = q?.lt('total', 0);
        else q = q?.eq('estado', filtros?.estado);
      }
      if (advFiltros?.vendedor) q = q?.or(`vendedor_nombre.ilike.%${advFiltros?.vendedor}%,vendedor_codigo.ilike.%${advFiltros?.vendedor}%`);
      if (advFiltros?.tipoOp === 'ventas') q = q?.gte('total', 0);
      if (advFiltros?.tipoOp === 'devoluciones') q = q?.lt('total', 0);
      if (advFiltros?.importeMin) q = q?.gte('total', parseFloat(advFiltros?.importeMin));
      if (advFiltros?.importeMax) q = q?.lte('total', parseFloat(advFiltros?.importeMax));
      if (advFiltros?.factura) q = q?.ilike('numero_factura', `%${advFiltros?.factura}%`);
      if (advFiltros?.conFactura === 'si') q = q?.not('numero_factura', 'is', null);
      if (advFiltros?.conFactura === 'no') q = q?.is('numero_factura', null);
      if (advFiltros?.cliente) q = q?.or(`cliente_nombre.ilike.%${advFiltros?.cliente}%,cliente_nif.ilike.%${advFiltros?.cliente}%,cliente_codigo.ilike.%${advFiltros?.cliente}%`);
      if (filtros?.ticket) q = q?.or(`numero_ticket.ilike.%${filtros?.ticket}%,epelsa_id::text.ilike.%${filtros?.ticket}%`);
      if (filtros?.globalSearch) q = q?.or(`numero_ticket.ilike.%${filtros?.globalSearch}%,numero_factura.ilike.%${filtros?.globalSearch}%,vendedor_nombre.ilike.%${filtros?.globalSearch}%,cliente_nombre.ilike.%${filtros?.globalSearch}%`);

      // Apply hora filter
      if (advFiltros?.horaDesde || advFiltros?.horaHasta) {
        const dateRef = filtros?.fechaDesde || today;
        if (advFiltros?.horaDesde) q = q?.gte('fecha', `${dateRef}T${advFiltros?.horaDesde}:00${offset}`);
        if (advFiltros?.horaHasta) q = q?.lte('fecha', `${dateRef}T${advFiltros?.horaHasta}:59${offset}`);
      }

      if (ticketIdsFromLines) q = q?.in('id', ticketIdsFromLines);
      if (ticketIdsFromPayment) q = q?.in('id', ticketIdsFromPayment);

      const { data: ticketData, error } = await q?.limit(5000);
      if (error) throw error;

      setConexionOk(true);
      const lista = ticketData || [];
      const ventasHoy = lista?.reduce((s, t) => s + (t?.total ?? 0), 0);
      const ticketsHoyCount = lista?.length;
      const ticketsValidos = lista?.filter(t => (t?.total ?? 0) > 0);
      const ticketMedio = ticketsValidos?.length > 0
        ? ticketsValidos?.reduce((s, t) => s + (t?.total ?? 0), 0) / ticketsValidos?.length : 0;

      let ticketIds = lista?.map(t => t?.id);
      let efectivo = 0, tarjeta = 0, bizum = 0;

      if (ticketIds?.length > 0) {
        const { data: pagosData } = await tiendaDb?.from('epelsa_ticket_pagos')
          ?.select('ticket_id, metodo_codigo, metodo_nombre, importe_entregado')
          ?.in('ticket_id', ticketIds);

        (pagosData || [])?.forEach(p => {
          const metodo = (p?.metodo_nombre || p?.metodo_codigo || '')?.toLowerCase()?.trim();
          const importe = p?.importe_entregado ?? 0;
          if (['efectivo', 't.efectivo', 'cash']?.includes(metodo)) efectivo += importe;
          else if (['t.credito', 'credito', 'tarjeta', 'credit']?.includes(metodo)) tarjeta += importe;
          else if (metodo === 'bizum') bizum += importe;
        });

        if (efectivo === 0 && tarjeta === 0 && bizum === 0) {
          lista?.forEach(t => {
            const fp = normalizarFormaPago(t?.forma_pago);
            const importe = t?.total ?? 0;
            if (fp === 'Efectivo') efectivo += importe;
            else if (fp === 'Tarjeta') tarjeta += importe;
            else if (fp === 'Bizum') bizum += importe;
          });
        }
      }

      setKpis({ ventasHoy, ticketsHoy: ticketsHoyCount, ticketMedio, efectivo, tarjeta, bizum });
    } catch (err) {
      setConexionOk(false);
      console.error('Error cargando KPIs Epelsa:', err);
    } finally {
      setLoadingKpis(false);
    }
  }, [filtros, advFiltros, hasLineFilter, getTicketIdsFromLineFilters, getTicketIdsFromPaymentFilter]);

  // ── Cargar tickets ─────────────────────────────────────────────────────────
  const cargarTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const offset = madridOffset();
      const today = getTodayMadrid();

      // Resolve line-level ticket IDs
      let ticketIdsFromLines = null;
      if (hasLineFilter()) {
        ticketIdsFromLines = await getTicketIdsFromLineFilters();
        if (ticketIdsFromLines?.length === 0) {
          setTickets([]);
          setTotalCount(0);
          setConexionOk(true);
          setLoadingTickets(false);
          return;
        }
      }

      let ticketIdsFromPayment = null;
      if (advFiltros?.formaPago) {
        ticketIdsFromPayment = await getTicketIdsFromPaymentFilter();
        if (ticketIdsFromPayment?.length === 0) {
          setTickets([]);
          setTotalCount(0);
          setConexionOk(true);
          setLoadingTickets(false);
          return;
        }
      }

      let q = tiendaDb?.from('epelsa_tickets')
        ?.select('id, epelsa_id, numero_ticket, fecha, vendedor_nombre, vendedor_codigo, total, base_imponible, iva_importe, forma_pago, estado, tipo_ticket, lineas_numero, balanza_codigo, counter_codigo, counter_nombre, numero_factura, cliente_nombre, cliente_codigo, sincronizado_en', { count: 'exact' });

      // Date filters
      if (filtros?.fechaDesde) {
        const dt = buildDateTimeFilter(filtros?.fechaDesde, advFiltros?.horaDesde, false);
        q = q?.gte('fecha', dt);
      }
      if (filtros?.fechaHasta) {
        const dt = buildDateTimeFilter(filtros?.fechaHasta, advFiltros?.horaHasta, true);
        q = q?.lte('fecha', dt);
      }
      // If no date filter, no restriction (show all)

      if (filtros?.ticket) q = q?.or(`numero_ticket.ilike.%${filtros?.ticket}%,epelsa_id::text.ilike.%${filtros?.ticket}%`);
      if (filtros?.caja) q = q?.or(`balanza_codigo.ilike.%${filtros?.caja}%,counter_codigo.ilike.%${filtros?.caja}%,counter_nombre.ilike.%${filtros?.caja}%`);
      if (filtros?.estado) {
        if (filtros?.estado === 'DEVOLUCION') q = q?.lt('total', 0);
        else q = q?.eq('estado', filtros?.estado);
      }
      if (filtros?.globalSearch) {
        q = q?.or(`numero_ticket.ilike.%${filtros?.globalSearch}%,numero_factura.ilike.%${filtros?.globalSearch}%,vendedor_nombre.ilike.%${filtros?.globalSearch}%,cliente_nombre.ilike.%${filtros?.globalSearch}%`);
      }

      if (advFiltros?.vendedor) q = q?.or(`vendedor_nombre.ilike.%${advFiltros?.vendedor}%,vendedor_codigo.ilike.%${advFiltros?.vendedor}%`);
      if (advFiltros?.tipoOp === 'ventas') q = q?.gte('total', 0);
      if (advFiltros?.tipoOp === 'devoluciones') q = q?.lt('total', 0);
      if (advFiltros?.importeMin) q = q?.gte('total', parseFloat(advFiltros?.importeMin));
      if (advFiltros?.importeMax) q = q?.lte('total', parseFloat(advFiltros?.importeMax));
      if (advFiltros?.factura) q = q?.ilike('numero_factura', `%${advFiltros?.factura}%`);
      if (advFiltros?.conFactura === 'si') q = q?.not('numero_factura', 'is', null);
      if (advFiltros?.conFactura === 'no') q = q?.is('numero_factura', null);
      if (advFiltros?.cliente) q = q?.or(`cliente_nombre.ilike.%${advFiltros?.cliente}%,cliente_nif.ilike.%${advFiltros?.cliente}%,cliente_codigo.ilike.%${advFiltros?.cliente}%`);

      if (ticketIdsFromLines) q = q?.in('id', ticketIdsFromLines);
      if (ticketIdsFromPayment) q = q?.in('id', ticketIdsFromPayment);

      // Sorting
      const sortMap = { fecha: 'fecha', ticket: 'numero_ticket', total: 'total' };
      const dbSort = sortMap?.[sortField] || 'fecha';
      q = q?.order(dbSort, { ascending: sortDir === 'asc' });

      // Pagination
      const from = (page - 1) * pageSize;
      q = q?.range(from, from + pageSize - 1);

      const { data, error, count } = await q;
      if (error) throw error;
      setTickets(data || []);
      setTotalCount(count ?? 0);
      setConexionOk(true);
    } catch (err) {
      setConexionOk(false);
      console.error('Error cargando tickets Epelsa:', err);
    } finally {
      setLoadingTickets(false);
    }
  }, [filtros, advFiltros, page, pageSize, sortField, sortDir, hasLineFilter, getTicketIdsFromLineFilters, getTicketIdsFromPaymentFilter]);

  // ── Cargar vista artículos ─────────────────────────────────────────────────
  const cargarLineas = useCallback(async () => {
    if (vistaMode !== 'articulos') return;
    setLoadingTickets(true);
    try {
      const offset = madridOffset();

      // First get matching ticket IDs
      let ticketIds = null;
      const needsTicketFilter = filtros?.fechaDesde || filtros?.fechaHasta || filtros?.ticket ||
        filtros?.caja || filtros?.estado || filtros?.globalSearch ||
        advFiltros?.vendedor || advFiltros?.tipoOp || advFiltros?.importeMin || advFiltros?.importeMax ||
        advFiltros?.factura || advFiltros?.conFactura || advFiltros?.cliente || advFiltros?.formaPago;

      if (needsTicketFilter) {
        let tq = tiendaDb?.from('epelsa_tickets')?.select('id');
        if (filtros?.fechaDesde) tq = tq?.gte('fecha', `${filtros?.fechaDesde}T00:00:00${offset}`);
        if (filtros?.fechaHasta) tq = tq?.lte('fecha', `${filtros?.fechaHasta}T23:59:59${offset}`);
        if (filtros?.ticket) tq = tq?.or(`numero_ticket.ilike.%${filtros?.ticket}%,epelsa_id::text.ilike.%${filtros?.ticket}%`);
        if (filtros?.caja) tq = tq?.or(`balanza_codigo.ilike.%${filtros?.caja}%,counter_codigo.ilike.%${filtros?.caja}%`);
        if (filtros?.estado === 'DEVOLUCION') tq = tq?.lt('total', 0);
        else if (filtros?.estado) tq = tq?.eq('estado', filtros?.estado);
        if (filtros?.globalSearch) tq = tq?.or(`numero_ticket.ilike.%${filtros?.globalSearch}%,vendedor_nombre.ilike.%${filtros?.globalSearch}%`);
        if (advFiltros?.vendedor) tq = tq?.or(`vendedor_nombre.ilike.%${advFiltros?.vendedor}%,vendedor_codigo.ilike.%${advFiltros?.vendedor}%`);
        if (advFiltros?.tipoOp === 'ventas') tq = tq?.gte('total', 0);
        if (advFiltros?.tipoOp === 'devoluciones') tq = tq?.lt('total', 0);
        if (advFiltros?.importeMin) tq = tq?.gte('total', parseFloat(advFiltros?.importeMin));
        if (advFiltros?.importeMax) tq = tq?.lte('total', parseFloat(advFiltros?.importeMax));
        if (advFiltros?.factura) tq = tq?.ilike('numero_factura', `%${advFiltros?.factura}%`);
        if (advFiltros?.conFactura === 'si') tq = tq?.not('numero_factura', 'is', null);
        if (advFiltros?.conFactura === 'no') tq = tq?.is('numero_factura', null);
        if (advFiltros?.cliente) tq = tq?.or(`cliente_nombre.ilike.%${advFiltros?.cliente}%,cliente_nif.ilike.%${advFiltros?.cliente}%`);

        if (advFiltros?.formaPago) {
          const ids = await getTicketIdsFromPaymentFilter();
          if (ids?.length === 0) { setLineasView([]); setTotalCount(0); setLoadingTickets(false); return; }
          if (ids) tq = tq?.in('id', ids);
        }

        const { data: tData } = await tq?.limit(2000);
        ticketIds = (tData || [])?.map(r => r?.id);
        if (ticketIds?.length === 0) { setLineasView([]); setTotalCount(0); setLoadingTickets(false); return; }
      }

      let lq = tiendaDb?.from('epelsa_ticket_lineas')
        ?.select('id, ticket_id, articulo_codigo, descripcion, familia_codigo, cantidad, peso, precio_unitario, iva_porcentaje, total, sale_form, lote_bovino, precio_modificado, fecha_linea, epelsa_tickets!inner(fecha, numero_ticket, vendedor_nombre)', { count: 'exact' });

      if (ticketIds) lq = lq?.in('ticket_id', ticketIds);
      if (advFiltros?.articulo) lq = lq?.or(`descripcion.ilike.%${advFiltros?.articulo}%,articulo_codigo.ilike.%${advFiltros?.articulo}%`);
      if (advFiltros?.familias?.length > 0) lq = lq?.in('familia_codigo', advFiltros?.familias);
      if (advFiltros?.pesoMin) lq = lq?.gte('peso', parseFloat(advFiltros?.pesoMin));
      if (advFiltros?.pesoMax) lq = lq?.lte('peso', parseFloat(advFiltros?.pesoMax));
      if (advFiltros?.cantidadMin) lq = lq?.gte('cantidad', parseFloat(advFiltros?.cantidadMin));
      if (advFiltros?.cantidadMax) lq = lq?.lte('cantidad', parseFloat(advFiltros?.cantidadMax));
      if (advFiltros?.precioMin) lq = lq?.gte('precio_unitario', parseFloat(advFiltros?.precioMin));
      if (advFiltros?.precioMax) lq = lq?.lte('precio_unitario', parseFloat(advFiltros?.precioMax));
      if (advFiltros?.iva) lq = lq?.eq('iva_porcentaje', parseFloat(advFiltros?.iva));
      if (advFiltros?.precioMod === 'modificado') lq = lq?.eq('precio_modificado', true);
      if (advFiltros?.precioMod === 'normal') lq = lq?.eq('precio_modificado', false);
      if (advFiltros?.saleForm) lq = lq?.eq('sale_form', advFiltros?.saleForm);
      if (advFiltros?.lote) lq = lq?.ilike('lote_bovino', `%${advFiltros?.lote}%`);
      if (filtros?.globalSearch) lq = lq?.or(`descripcion.ilike.%${filtros?.globalSearch}%,articulo_codigo.ilike.%${filtros?.globalSearch}%`);

      // Sort for articulos view
      const artSortMap = { articulo: 'descripcion', familia: 'familia_codigo', precio: 'precio_unitario', peso: 'peso', cantidad: 'cantidad', fecha: 'fecha_linea' };
      const artDbSort = artSortMap?.[sortField] || 'fecha_linea';
      lq = lq?.order(artDbSort, { ascending: sortDir === 'asc', nullsFirst: false });

      const from = (page - 1) * pageSize;
      lq = lq?.range(from, from + pageSize - 1);

      const { data, error, count } = await lq;
      if (error) throw error;
      setLineasView(data || []);
      setTotalCount(count ?? 0);
      setConexionOk(true);
    } catch (err) {
      setConexionOk(false);
      console.error('Error cargando líneas Epelsa:', err);
    } finally {
      setLoadingTickets(false);
    }
  }, [vistaMode, filtros, advFiltros, page, pageSize, sortField, sortDir, getTicketIdsFromPaymentFilter]);

  const cargarConfig = useCallback(async () => {
    try { const cfg = await epelsaConfigService?.get(); setConfig(cfg); } catch { }
  }, []);

  const cargarNoVinculados = useCallback(async () => {
    try { const items = await epelsaLineasNoVinculadasService?.getAll(); setNoVinculados(items || []); } catch { }
  }, []);

  useEffect(() => { cargarConfig(); cargarOpcionesFiltros(); cargarNoVinculados(); }, []);

  useEffect(() => {
    if (vistaMode === 'tickets') { cargarTickets(); cargarKpis(); }
    else { cargarLineas(); cargarKpis(); }
  }, [cargarTickets, cargarLineas, cargarKpis, vistaMode]);

  const handleRefresh = () => {
    cargarConfig();
    cargarKpis();
    if (vistaMode === 'tickets') cargarTickets();
    else cargarLineas();
    cargarNoVinculados();
  };

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({ fechaDesde: '', fechaHasta: '', ticket: '', caja: '', estado: '', globalSearch: '' });
    setAdvFiltros({
      articulo: '', familias: [], vendedor: '', formaPago: '', tipoOp: '',
      importeMin: '', importeMax: '', horaDesde: '', horaHasta: '',
      pesoMin: '', pesoMax: '', cantidadMin: '', cantidadMax: '',
      precioMin: '', precioMax: '', iva: '', precioMod: '', saleForm: '',
      factura: '', conFactura: '', lote: '', cliente: '',
    });
    setPage(1);
  };

  const handleRemoveChip = (key, value, isAdv = false) => {
    if (isAdv) setAdvFiltros(prev => ({ ...prev, [key]: value }));
    else setFiltros(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const handleVincular = async (id, productoId) => {
    await epelsaLineasNoVinculadasService?.vincular(id, productoId);
    cargarNoVinculados();
  };

  const handleIgnorar = async (id) => {
    await epelsaLineasNoVinculadasService?.ignorar(id);
    cargarNoVinculados();
  };

  // Count active advanced filters
  const advFilterCount = Object.entries(advFiltros)?.filter(([k, v]) =>
    Array.isArray(v) ? v?.length > 0 : v !== ''
  )?.length;

  const sections = [
    { id: 'tickets', label: 'Ventas Epelsa', icon: 'Receipt' },
    { id: 'novinculados', label: `No vinculados${noVinculados?.length > 0 ? ` (${noVinculados?.length})` : ''}`, icon: 'LinkOff' },
    { id: 'conexion', label: 'Estado conexión', icon: 'Wifi' },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);
  const fromRecord = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const toRecord = Math.min(page * pageSize, totalCount);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {showEmergency && <EmergencySaleModal onClose={() => { setShowEmergency(false); handleRefresh(); }} />}
      {selectedTicket && <TicketDetailModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} />}
      {showAdvanced && (
        <AdvancedFiltersPanel
          advFiltros={advFiltros}
          setAdvFiltros={(v) => { setAdvFiltros(v); setPage(1); }}
          onClose={() => setShowAdvanced(false)}
          familiasDisponibles={familiasDisponibles}
          vendedoresDisponibles={vendedoresDisponibles}
          ivaDisponibles={ivaDisponibles}
          saleFormsDisponibles={saleFormsDisponibles}
        />
      )}
      {/* Top Bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
            <Icon name="Scale" size={16} style={{ color: ACCENT }} />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-lg">Visor Ventas Epelsa</h1>
            <p className="font-oranienbaum text-white/30 text-xs">Panel de control · Báscula TPV principal</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {conexionOk !== null && (
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
              style={{
                background: conexionOk ? `${ACCENT}10` : `${RED}10`,
                border: `1px solid ${conexionOk ? ACCENT : RED}30`,
                color: conexionOk ? ACCENT : RED,
              }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: conexionOk ? ACCENT : RED }} />
              {conexionOk ? 'Conectado' : 'Error de conexión'}
            </div>
          )}
          <button onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
            <Icon name="RefreshCw" size={13} />
            Actualizar
          </button>
          <button onClick={() => setShowEmergency(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: `${ORANGE}15`, border: `1px solid ${ORANGE}30`, color: ORANGE }}>
            <Icon name="AlertTriangle" size={13} />
            Venta emergencia
          </button>
        </div>
      </div>
      <div className="p-5 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <KpiCard label={filtros?.fechaDesde || filtros?.fechaHasta ? 'Ventas periodo' : 'Ventas hoy'} value={loadingKpis ? '...' : `${fmt(kpis?.ventasHoy)} €`} icon="TrendingUp" color={ACCENT} sub={loadingKpis ? undefined : `${kpis?.ticketsHoy} tickets`} />
          <KpiCard label="Tickets" value={loadingKpis ? '...' : kpis?.ticketsHoy} icon="Receipt" color={BLUE} />
          <KpiCard label="Ticket medio" value={loadingKpis ? '...' : `${fmt(kpis?.ticketMedio)} €`} icon="BarChart2" color={PURPLE} />
          <KpiCard label="Efectivo" value={loadingKpis ? '...' : `${fmt(kpis?.efectivo)} €`} icon="Banknote" color={ACCENT} />
          <KpiCard label="Tarjeta" value={loadingKpis ? '...' : `${fmt(kpis?.tarjeta)} €`} icon="CreditCard" color={BLUE} />
          <KpiCard label="Bizum" value={loadingKpis ? '...' : `${fmt(kpis?.bizum)} €`} icon="Shuffle" color={PURPLE} />
          <KpiCard label="Errores sync" value="Sin datos" icon="AlertCircle" color="rgba(255,255,255,0.3)" sub={noVinculados?.length > 0 ? `${noVinculados?.length} sin vincular` : undefined} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl w-fit"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {sections?.map(s => (
            <button key={s?.id} onClick={() => setActiveSection(s?.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-oranienbaum transition-all"
              style={activeSection === s?.id
                ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }
                : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
              <Icon name={s?.icon} size={13} />
              {s?.label}
            </button>
          ))}
        </div>

        {/* ── SECCIÓN: TICKETS ──────────────────────────────────────────────── */}
        {activeSection === 'tickets' && (
          <div className="space-y-4">
            {/* Global search + basic filters */}
            <div className="rounded-2xl p-4 space-y-3"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {/* Global search bar */}
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="text" placeholder="Buscar ventas, artículos, tickets, vendedores, facturas..."
                  value={filtros?.globalSearch}
                  onChange={e => handleFiltroChange('globalSearch', e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }} />
              </div>

              {/* Basic filters row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 items-end">
                <div>
                  <label className="font-oranienbaum text-white/35 text-[10px] mb-1 block">Fecha desde</label>
                  <input type="date" value={filtros?.fechaDesde}
                    onChange={e => handleFiltroChange('fechaDesde', e?.target?.value)}
                    className="w-full px-2 py-2 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="font-oranienbaum text-white/35 text-[10px] mb-1 block">Fecha hasta</label>
                  <input type="date" value={filtros?.fechaHasta}
                    onChange={e => handleFiltroChange('fechaHasta', e?.target?.value)}
                    className="w-full px-2 py-2 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="font-oranienbaum text-white/35 text-[10px] mb-1 block">Nº Ticket</label>
                  <input type="text" placeholder="2165, 2166..." value={filtros?.ticket}
                    onChange={e => handleFiltroChange('ticket', e?.target?.value)}
                    className="w-full px-2 py-2 rounded-lg text-xs font-oranienbaum text-white/70 placeholder-white/20 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                </div>
                <div>
                  <label className="font-oranienbaum text-white/35 text-[10px] mb-1 block">Caja/Balanza</label>
                  <input type="text" placeholder="Caja / balanza" value={filtros?.caja}
                    onChange={e => handleFiltroChange('caja', e?.target?.value)}
                    className="w-full px-2 py-2 rounded-lg text-xs font-oranienbaum text-white/70 placeholder-white/20 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                </div>
                <div>
                  <label className="font-oranienbaum text-white/35 text-[10px] mb-1 block">Estado</label>
                  <select value={filtros?.estado}
                    onChange={e => handleFiltroChange('estado', e?.target?.value)}
                    className="w-full px-2 py-2 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    <option value="VALIDO">Válido</option>
                    <option value="ANULADO">Anulado</option>
                    <option value="DEVOLUCION">Devolución</option>
                  </select>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={handleLimpiarFiltros}
                    className="flex-1 py-2 rounded-lg text-xs font-oranienbaum transition-all"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
                    Limpiar
                  </button>
                  <button onClick={handleRefresh}
                    className="flex-1 py-2 rounded-lg text-xs font-oranienbaum transition-all"
                    style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}25`, color: BLUE }}>
                    Actualizar
                  </button>
                </div>
                <div>
                  <button onClick={() => setShowAdvanced(true)}
                    className="w-full py-2 rounded-lg text-xs font-oranienbaum transition-all flex items-center justify-center gap-1.5"
                    style={advFilterCount > 0
                      ? { background: `${PURPLE}20`, border: `1px solid ${PURPLE}40`, color: PURPLE }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.55)' }}>
                    <Icon name="SlidersHorizontal" size={12} />
                    Filtros avanzados
                    {advFilterCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                        style={{ background: PURPLE, color: '#000' }}>
                        {advFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Active filter chips */}
            <FilterChips
              filtros={filtros}
              advFiltros={advFiltros}
              onRemove={handleRemoveChip}
              onClearAll={handleLimpiarFiltros}
            />

            {/* Vista toggle + counter + page size */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {/* Tickets / Artículos toggle */}
                <div className="flex gap-1 p-1 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {[{ v: 'tickets', l: 'Tickets', i: 'Receipt' }, { v: 'articulos', l: 'Artículos', i: 'Package' }]?.map(m => (
                    <button key={m?.v} onClick={() => { setVistaMode(m?.v); setPage(1); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                      style={vistaMode === m?.v
                        ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }
                        : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
                      <Icon name={m?.i} size={12} />
                      {m?.l}
                    </button>
                  ))}
                </div>
                {/* Counter */}
                {!loadingTickets && (
                  <span className="font-oranienbaum text-white/40 text-xs">
                    {vistaMode === 'tickets' ? `${totalCount} tickets encontrados` : `${totalCount} líneas encontradas`}
                  </span>
                )}
              </div>
              {/* Page size selector */}
              <div className="flex items-center gap-2">
                <span className="font-oranienbaum text-white/30 text-xs">Por página:</span>
                {[25, 50, 100]?.map(ps => (
                  <button key={ps} onClick={() => { setPageSize(ps); setPage(1); }}
                    className="px-2.5 py-1 rounded-lg text-xs font-oranienbaum transition-all"
                    style={pageSize === ps
                      ? { background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
                    {ps}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {loadingTickets ? (
                <div className="flex items-center justify-center py-16 gap-3">
                  <div className="w-6 h-6 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                  <span className="font-oranienbaum text-white/30 text-sm">Cargando...</span>
                </div>
              ) : vistaMode === 'tickets' ? (
                tickets?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Icon name="Receipt" size={28} className="text-white/15" />
                    <p className="font-oranienbaum text-white/30 text-sm">No se encontraron tickets</p>
                    {conexionOk === false && <p className="font-oranienbaum text-red-400/60 text-xs">Error de conexión con Supabase</p>}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <SortHeader label="Nº Ticket" field="ticket" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                            <SortHeader label="Fecha" field="fecha" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                            <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Hora</th>
                            <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Vendedor</th>
                            <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Nº Líneas</th>
                            <SortHeader label="Total" field="total" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                            <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Forma pago</th>
                            <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Estado</th>
                            <th className="px-4 py-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {tickets?.map((t, i) => {
                            const isDev = esDevolucion(t);
                            return (
                              <tr key={t?.id}
                                className="transition-colors cursor-pointer hover:bg-white/[0.02]"
                                style={{
                                  borderBottom: i < tickets?.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                  background: isDev ? 'rgba(251,146,60,0.03)' : undefined,
                                }}
                                onClick={() => setSelectedTicket(t)}>
                                <td className="px-4 py-3">
                                  <span className="font-oranienbaum text-white/80 text-xs">{t?.numero_ticket || t?.epelsa_id || '—'}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-oranienbaum text-white/50 text-xs">{fmtDateOnly(t?.fecha)}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-oranienbaum text-white/50 text-xs">{fmtTime(t?.fecha)}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-oranienbaum text-white/50 text-xs">{t?.vendedor_nombre || t?.vendedor_codigo || '—'}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-oranienbaum text-white/50 text-xs">{t?.lineas_numero ?? '—'}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`font-oranienbaum font-semibold text-xs ${isDev ? 'text-orange-400' : 'text-white'}`}>
                                    {fmt(t?.total)} €
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-oranienbaum text-white/50 text-xs">{normalizarFormaPago(t?.forma_pago)}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <EstadoBadge estado={t?.estado} isDev={isDev} />
                                </td>
                                <td className="px-4 py-3">
                                  <Icon name="ChevronRight" size={13} className="text-white/25" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )
              ) : (
                // Vista Artículos
                (lineasView?.length === 0 ? (<div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Icon name="Package" size={28} className="text-white/15" />
                  <p className="font-oranienbaum text-white/30 text-sm">No se encontraron líneas</p>
                </div>) : (<div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <SortHeader label="Fecha" field="fecha" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Hora</th>
                        <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Nº Ticket</th>
                        <SortHeader label="Código" field="articulo" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Descripción</th>
                        <SortHeader label="Familia" field="familia" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Cantidad" field="cantidad" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Peso" field="peso" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Precio" field="precio" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                        <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">IVA</th>
                        <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Total</th>
                        <th className="px-4 py-3 text-left font-oranienbaum text-white/35 text-[10px] uppercase tracking-widest">Vendedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineasView?.map((l, i) => (
                        <tr key={l?.id}
                          className="transition-colors hover:bg-white/[0.02]"
                          style={{ borderBottom: i < lineasView?.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/50 text-xs">{fmtDateOnly(l?.epelsa_tickets?.fecha || l?.fecha_linea)}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/50 text-xs">{fmtTime(l?.epelsa_tickets?.fecha || l?.fecha_linea)}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/60 text-xs">{l?.epelsa_tickets?.numero_ticket || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/50 text-xs">{l?.articulo_codigo || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/80 text-xs">{l?.descripcion || '—'}</span>
                            {l?.lote_bovino && <span className="ml-2 font-oranienbaum text-amber-300/60 text-[10px]">L:{l?.lote_bovino}</span>}
                            {l?.precio_modificado && <span className="ml-1 font-oranienbaum text-orange-300/60 text-[10px]">★</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/40 text-xs">{l?.familia_codigo || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/50 text-xs">{l?.cantidad ?? '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-amber-400/70 text-xs">{l?.peso != null && l?.peso !== 0 ? `${l?.peso} kg` : '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/50 text-xs">{l?.precio_unitario != null ? `${fmt(l?.precio_unitario)} €` : '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/40 text-xs">{l?.iva_porcentaje != null ? `${l?.iva_porcentaje}%` : '—'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum font-semibold text-white text-xs">{fmt(l?.total)} €</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-oranienbaum text-white/40 text-xs">{l?.epelsa_tickets?.vendedor_nombre || '—'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>))
              )}

              {/* Pagination footer */}
              {!loadingTickets && totalCount > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <span className="font-oranienbaum text-white/30 text-xs">
                    {fromRecord}–{toRecord} de {totalCount} {vistaMode === 'tickets' ? 'tickets' : 'líneas'}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                      className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                      ← Anterior
                    </button>
                    <span className="px-3 py-1.5 font-oranienbaum text-white/30 text-xs">
                      {page} / {totalPages || 1}
                    </span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                      className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                      Siguiente →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SECCIÓN: PRODUCTOS NO VINCULADOS ─────────────────────────────── */}
        {activeSection === 'novinculados' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-oranienbaum font-semibold text-white text-base">Productos no vinculados</h2>
                <p className="font-oranienbaum text-white/35 text-xs mt-0.5">
                  Líneas recibidas desde Epelsa sin coincidencia en el catálogo ERP. Pendiente de asociación manual.
                </p>
              </div>
              <button onClick={cargarNoVinculados}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                <Icon name="RefreshCw" size={12} />
                Actualizar
              </button>
            </div>
            <ProductosNoVinculados items={noVinculados} onVincular={handleVincular} onIgnorar={handleIgnorar} />
          </div>
        )}

        {/* ── SECCIÓN: ESTADO CONEXIÓN ──────────────────────────────────────── */}
        {activeSection === 'conexion' && (
          <div className="space-y-4">
            <div>
              <h2 className="font-oranienbaum font-semibold text-white text-base">Estado de conexión Epelsa</h2>
              <p className="font-oranienbaum text-white/35 text-xs mt-0.5">Estado basado en la capacidad real de consultar Supabase</p>
            </div>
            <ConexionEpelsa config={config} conexionOk={conexionOk !== false} onRefresh={handleRefresh} />
            <div className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-semibold text-white text-sm mb-3">Protocolos disponibles</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'REST API', icon: 'Globe', desc: 'HTTP/HTTPS endpoint', color: BLUE },
                  { label: 'TCP/IP', icon: 'Network', desc: 'Conexión directa socket', color: ACCENT },
                  { label: 'Archivo exportado', icon: 'FileText', desc: 'CSV / XML / JSON', color: GOLD },
                  { label: 'EpelSoft local', icon: 'Cpu', desc: 'Programa intermedio', color: PURPLE },
                ]?.map((p, i) => (
                  <div key={i} className="rounded-xl p-3"
                    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${p?.color}15` }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                      style={{ background: `${p?.color}15`, border: `1px solid ${p?.color}25` }}>
                      <Icon name={p?.icon} size={14} style={{ color: p?.color }} />
                    </div>
                    <p className="font-oranienbaum text-white/70 text-xs font-semibold">{p?.label}</p>
                    <p className="font-oranienbaum text-white/30 text-[10px] mt-0.5">{p?.desc}</p>
                    <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[9px] font-oranienbaum"
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)' }}>
                      Pendiente configurar
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TPVFisico;
